import { parseSSEEventData } from './sseEventParsers.ts';
import LlmProcessingError from '../components/exceptions/LlmProcessingError.jsx';

/**
 * Creates a configured SSE stream parser with all dependencies injected.
 * Return a function that can be vcalled with just the stream reader/decoder.
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.setMessages - Function to update messages state
 * @param {Object} config.messageIdRef - Ref object containing current message ID
 * @returns {Function} Parser function that takes (reader, decoder, restoreInput)
 */
export function createSSEStreamParser({ setMessages, messageIdRef }) {
  // State tracking for current part being streamed
  let currentPart = null; // { type: 'thinking' | 'answer' | 'tool_use' | 'tool_result', part: ThinkingPart | AnswerPart | ToolUsePart | ToolResultPart }
  let toolUseJsonBuffer = ''; // Accumulated JSON string for tool_use
  let inSubAgent = false; // Whether we're inside a sub-agent execution
  let subAgentPartsStartIndex = -1; // message_parts index when sub-agent started
  let currentLegIndex = 1; // Current agentic loop leg index

  /**
   * Updates the message by immutably modifying the message_parts array.
   */
  const updateMessage = (updater) => {
    setMessages(prevMessages => {
      const msgIndex = prevMessages.findIndex(m => m.id === messageIdRef.current);
      if (msgIndex === -1) return prevMessages;

      const newMessages = prevMessages.slice();
      const prevMsg = prevMessages[msgIndex];

      // Apply the updater function to get new message_parts
      const updatedMsg = {
        ...prevMsg,
        message_parts: updater(prevMsg.message_parts || [])
      }

      newMessages[msgIndex] = updatedMsg;
      return newMessages;
    });
  };

  /**
   * Cleans up partial response on error or incomplete stream
   */
  const cleanupPartialResponse = () => {
    setMessages((prev) => {
      const tempMessages = [...prev];

      if (tempMessages.length > 0) {
        // Remove empty assistant message
        const lastMessage = tempMessages[tempMessages.length - 1];
        if (lastMessage && lastMessage.role === "assistant" && (!lastMessage.message_parts || lastMessage.message_parts.length === 0)) {
          tempMessages.pop();

          // Remove user message that triggered the failed response
          if (tempMessages.length > 0) {
            const newLastMessage = tempMessages[tempMessages.length - 1];
            if (newLastMessage && newLastMessage.role === "user") {
              tempMessages.pop();
            }
          }
        }
      }

      return tempMessages;
    });
  };

  /**
   * Adds the current part to message_parts (for streaming)
   */
  const addStreamingPart = () => {
    if (!currentPart) return;

    const { type, part } = currentPart;

    updateMessage((messageParts) => {
      // Ensure messageParts is always an array
      if (!Array.isArray(messageParts)) {
        console.warn('addStreamingPart: messageParts is not an array', messageParts);
        return [];
      }

      // Add it as a new part
      let partToAdd;

      if (type === 'thinking') {
        partToAdd = {
          type: 'thinking',
          content: part.content,
          metadata: { ...(part.metadata || {}), leg_index: currentLegIndex },
        };
      } else if (type === 'answer') {
        partToAdd = {
          type: 'answer',
          content: part.content,
          metadata: { ...(part.metadata || {}), leg_index: currentLegIndex },
        };
      } else if (type === 'tool_use') {
        // Tool use parts are now added via addToolUsePart() which handles parameters
        // This function is kept for thinking/answer parts
        return messageParts;
      }

      if (partToAdd) {
        return [...messageParts, partToAdd];
      }

      return messageParts;
    });
  };

  /**
   * Adds or updates a tool_use part in message_parts
   * Updates existing part if same tool_use_id exists, otherwise adds new one
   * Can be called with null parameters (will be added later via tool_use_end)
   */
  const addToolUsePart = () => {
    if (!currentPart || currentPart.type !== 'tool_use') return;

    const { part } = currentPart;

    let parameters = null;
    if (toolUseJsonBuffer) {
      try {
        parameters = JSON.parse(toolUseJsonBuffer);
      } catch (error) {
        console.error('Failed to parse tool use parameters:', error);
        parameters = {};
      }
    }
    // If no parameters yet, that's okay - we'll add it with null parameters
    // Parameters can be updated later via tool_use_end

    const toolUsePart = {
      type: 'tool_use',
      tool_use_id: part.tool_use_id,
      name: part.name,
      tool_type: part.tool_type,
      parameters: parameters, // Can be null if not yet available
      metadata: { ...(part.metadata || {}), leg_index: currentLegIndex },
    };

    updateMessage((messageParts) => {
      // Check if a tool_use part with the same tool_use_id already exists
      const existingIndex = messageParts.findIndex(
        p => p.type === 'tool_use' && p.tool_use_id === part.tool_use_id
      );

      if (existingIndex !== -1) {
        // Update existing part instead of adding duplicate
        const updatedParts = [...messageParts];
        updatedParts[existingIndex] = {
          ...updatedParts[existingIndex],
          ...toolUsePart, // Update with new parameters and any other changes
        };
        return updatedParts;
      } else {
        // Add new part if it doesn't exist
        return [...messageParts, toolUsePart];
      }
    });
  }

  /**
   * Handles errors that occur during the stream parsing process
   */
  const handleStreamError = (error, restoreInput) => {
    // Treat aborted stream reads as AbortError so callers handle them gracefully
    if (error.name === 'AbortError' || 
        (error instanceof DOMException && error.message.includes('aborted'))) {
      const abortErr = new DOMException('The operation was aborted.', 'AbortError');
      throw abortErr;
    }

    // Provider errors (LlmProcessingError) — clean up empty placeholder messages
    // before propagating to ConversationPage for toast and state reset
    if (error.name === 'LlmProcessingError') {
      cleanupPartialResponse();
      throw error;
    }

    console.error('Error parsing SSE stream:', error);
    cleanupPartialResponse();

    if (restoreInput) {
      restoreInput();
    }

    throw error;
  };

  /**
   * Updates a streaming part's content in real-time
   */
  const updateStreamingPart = (content) => {
    if (!currentPart) return; // Safety check

    // Capture currentPart values before async callback
    // (updateMessage is async, so currentPart might change by the time callback executes)
    const partType = currentPart.type;
    const partMetadata = currentPart.part.metadata;
    const currentIndex = partMetadata?.index;
    const capturedLegIndex = currentLegIndex;
    const durationMs = currentPart.type === 'thinking' ? currentPart.part.duration_ms : null;

    updateMessage((messageParts) => {
      // Ensure messageParts is always an array
      if (!Array.isArray(messageParts)) {
        console.warn('updateStreamingPart: messageParts is not an array', messageParts);
        return [];
      }

      // Find the part to update by matching type, metadata index, AND leg_index
      // leg_index prevents cross-leg overwrites (e.g., leg 2's thinking overwriting leg 1's)
      const partIndex = messageParts.findIndex(part => {
        const matchesType = part.type === partType;
        const matchesLeg = part.metadata?.leg_index === capturedLegIndex;
        const matchesIndex = currentIndex !== undefined 
          ? part.metadata?.index === currentIndex
          : true; // If no index, match by being the last part of this type
        
        return matchesType && matchesLeg && matchesIndex;
      });

      if (partIndex === -1) {
        // Fallback: use last part of matching type (original behavior)
        const lastIndex = messageParts.length - 1;
        if (lastIndex < 0) return messageParts;
        
        const lastPart = messageParts[lastIndex];
        if (lastPart && lastPart.type === partType) {
          const updatedParts = [...messageParts];
          updatedParts[lastIndex] = {
            ...lastPart,
            content: content,
            ...(durationMs !== null ? { duration_ms: durationMs } : {})
          };
          return updatedParts;
        }
        return messageParts;
      }

      // Update the found part
      const updatedParts = [...messageParts];
      updatedParts[partIndex] = {
        ...messageParts[partIndex],
        content: content,
        ...(durationMs !== null ? { duration_ms: durationMs } : {})
      };
      return updatedParts;
    });
  };

  /**
   * Finalizes current part (resets current part, does not add to array)
   */
  const finalizeCurrentPart = () => {
    // Just reset - the part is already in message_parts from streaming
    currentPart = null;
    toolUseJsonBuffer = '';
  };

  /**
   * Safety check: warns if currentPart exists when starting a new part
   */
  const checkAndWarnUnfinalizedPart = (newPartType) => {
    if (currentPart) {
      console.warn(
        `Starting ${newPartType} but previous ${currentPart.type} part was not finalized. ` +
        `This suggests a missing *_end event. Finalizing previous part.`
      );
      finalizeCurrentPart();
    }
  };

  /**
   * Processes SSE events and updates message accordingly
   */
  const processSSEEvent = (event) => {
    if (!messageIdRef.current) return;

    try {
      switch (event.type) {
        case 'thinking_start':
          // Skip sub-agent events — they're consumed server-side, only the final result matters
          if (inSubAgent) break;
          checkAndWarnUnfinalizedPart('thinking');
          currentPart = {
            type: 'thinking',
            part: {
              content: '',
              duration_ms: null,
              metadata: event.data.metadata || null,
            }
          };
          // Add the part immediately so it can be updated during streaming
          addStreamingPart();
          break;

        case 'thinking':
          if (inSubAgent) break;
          if (currentPart?.type === 'thinking') {
            currentPart.part.content += event.data.chunk || '';
            // Update the streaming part in real-time
            updateStreamingPart(currentPart.part.content);
          }
          break;

        case 'thinking_end':
          if (inSubAgent) break;
          if (currentPart?.type === 'thinking') {
            const finalContent = event.data.content || currentPart.part.content;
            currentPart.part.duration_ms = event.data.duration_ms || null;
            // Update the streaming part in real-time
            updateStreamingPart(finalContent);
            finalizeCurrentPart();
          }
          break;

        case 'answer_start':
          checkAndWarnUnfinalizedPart('answer');

          // Create new answer part
          currentPart = {
            type: 'answer',
            part: {
              content: '',
              metadata: event.data.metadata || null,
            }
          };
          // Add the part immediately so it can be updated during streaming
          addStreamingPart();
          break;

        case 'answer':
          if (currentPart?.type === 'answer') {
            currentPart.part.content += event.data.chunk || '';
            // Update the streaming part in real-time
            updateStreamingPart(currentPart.part.content);
          }
          break;

        case 'answer_end':
          if (currentPart?.type === 'answer') {
            // Final update before resetting
            updateStreamingPart(currentPart.part.content);
            finalizeCurrentPart();
          }
          break;

        case 'tool_use_start':
          checkAndWarnUnfinalizedPart('tool_use');

          // Create new tool use part
          currentPart = {
            type: 'tool_use',
            part: {
              tool_use_id: event.data.tool_use_id,
              name: event.data.name,
              tool_type: event.data.type,
              parameters: null,
              metadata: event.data.metadata || null,
            }
          };

          toolUseJsonBuffer = '';
          // IMPORTANT: Add the tool_use part immediately (even without parameters)
          // Parameters will be added later via tool_use_end or we'll handle it in tool_use_result
          addToolUsePart(); // Add it now with null parameters
          break;

        case 'tool_use':
          if (currentPart?.type === 'tool_use') {
            toolUseJsonBuffer += event.data.chunk || '';
          }
          break;

        case 'tool_use_end':
          if (currentPart?.type === 'tool_use') {
            // Add the completed tool_use part to message_parts
            addToolUsePart();
            // Then finalize (reset currentPart)
            finalizeCurrentPart();
          }
          break;

        case 'tool_use_result':
          // First, check if there's an active tool_use part that needs to be finalized
          if (currentPart?.type === 'tool_use') {
            // If we have an active tool_use, we need to update it with any parameters
            // But since GPT sends tool_use_result directly, we'll just finalize the tool_use
            finalizeCurrentPart();
          }
          
          // Create tool result part and add to message_parts
          // Skip if a tool_result with this tool_use_id already exists
          // (sub_agent_end may have already created it)
          updateMessage((messageParts) => {
            const alreadyExists = messageParts.some(
              p => p.type === 'tool_result' && p.tool_use_id === event.data.tool_use_id
            );
            if (alreadyExists) return messageParts;

            const toolResultPart = {
              type: 'tool_result',
              tool_use_id: event.data.tool_use_id,
              name: event.data.name,
              result: event.data.result,
              metadata: { ...(event.data.metadata || {}), leg_index: currentLegIndex },
            };

            return [...messageParts, toolResultPart];
          });
          break;

        case 'sub_agent_start':
          // A sub-agent is being executed — record the current parts count
          // so we can replace intermediate answer parts with the final result.
          inSubAgent = true;
          updateMessage((messageParts) => {
            subAgentPartsStartIndex = messageParts.length;

            // Mark the matching tool_use part as sub_agent for proper rendering
            const toolUseId = event.data.tool_use_id;
            const toolIndex = messageParts.findIndex(
              p => p.type === 'tool_use' && p.tool_use_id === toolUseId
            );
            if (toolIndex !== -1) {
              const updatedParts = [...messageParts];
              updatedParts[toolIndex] = {
                ...updatedParts[toolIndex],
                tool_type: 'sub_agent',
                instruction: event.data.instruction || null,
                sub_agent_model: event.data.model || null,
                streaming_content: '',
              };
              return updatedParts;
            }

            return messageParts;
          });
          break;

        case 'sub_agent_progress':
          // Live streaming text from the sub-agent — update the tool_use
          // part's streaming_content instead of creating an answer part
          if (inSubAgent && event.data.tool_use_id) {
            const chunk = event.data.chunk || '';
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex !== -1) {
                const updatedParts = [...messageParts];
                updatedParts[toolIndex] = {
                  ...updatedParts[toolIndex],
                  streaming_content: (updatedParts[toolIndex].streaming_content || '') + chunk,
                };
                return updatedParts;
              }
              return messageParts;
            });
          }
          break;

        case 'sub_agent_tool_start':
          if (inSubAgent && event.data.tool_use_id) {
            const toolStartName = event.data.name || '';
            const toolParams = event.data.parameters
              ? (typeof event.data.parameters === 'string'
                ? (() => { try { return JSON.parse(event.data.parameters); } catch { return null; } })()
                : event.data.parameters)
              : null;
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex === -1) return messageParts;
              const updatedParts = [...messageParts];
              const prev = updatedParts[toolIndex];
              const activity = [...(prev.streaming_activity || []), { type: 'tool', name: toolStartName, parameters: toolParams, status: 'active' }];
              updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
              return updatedParts;
            });
          }
          break;

        case 'sub_agent_tool_update':
          if (inSubAgent && event.data.tool_use_id) {
            const toolUpdateParams = event.data.parameters
              ? (typeof event.data.parameters === 'string'
                ? (() => { try { return JSON.parse(event.data.parameters); } catch { return null; } })()
                : event.data.parameters)
              : null;
            if (toolUpdateParams) {
              updateMessage((messageParts) => {
                const toolIndex = messageParts.findIndex(
                  p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
                );
                if (toolIndex === -1) return messageParts;
                const updatedParts = [...messageParts];
                const prev = updatedParts[toolIndex];
                const activity = (prev.streaming_activity || []).map((item, i, arr) =>
                  item.type === 'tool' && item.status === 'active' && i === arr.findLastIndex(a => a.type === 'tool' && a.status === 'active')
                    ? { ...item, parameters: toolUpdateParams }
                    : item
                );
                updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
                return updatedParts;
              });
            }
          }
          break;

        case 'sub_agent_tool_end':
          if (inSubAgent && event.data.tool_use_id) {
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex === -1) return messageParts;
              const updatedParts = [...messageParts];
              const prev = updatedParts[toolIndex];
              const activity = (prev.streaming_activity || []).map((item, i, arr) =>
                item.type === 'tool' && item.status === 'active' && i === arr.findLastIndex(a => a.type === 'tool' && a.status === 'active')
                  ? { ...item, status: 'done' }
                  : item
              );
              updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
              return updatedParts;
            });
          }
          break;

        case 'sub_agent_thinking_start':
          if (inSubAgent && event.data.tool_use_id) {
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex === -1) return messageParts;
              const updatedParts = [...messageParts];
              const prev = updatedParts[toolIndex];
              const activity = [...(prev.streaming_activity || []), { type: 'thinking', content: '', status: 'active' }];
              updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
              return updatedParts;
            });
          }
          break;

        case 'sub_agent_thinking':
          if (inSubAgent && event.data.tool_use_id) {
            const thinkChunk = event.data.chunk || '';
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex === -1) return messageParts;
              const updatedParts = [...messageParts];
              const prev = updatedParts[toolIndex];
              const activity = (prev.streaming_activity || []).map((item, i, arr) =>
                item.type === 'thinking' && item.status === 'active' && i === arr.findLastIndex(a => a.type === 'thinking' && a.status === 'active')
                  ? { ...item, content: item.content + thinkChunk }
                  : item
              );
              updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
              return updatedParts;
            });
          }
          break;

        case 'sub_agent_thinking_end':
          if (inSubAgent && event.data.tool_use_id) {
            updateMessage((messageParts) => {
              const toolIndex = messageParts.findIndex(
                p => p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id
              );
              if (toolIndex === -1) return messageParts;
              const updatedParts = [...messageParts];
              const prev = updatedParts[toolIndex];
              const activity = (prev.streaming_activity || []).map((item, i, arr) =>
                item.type === 'thinking' && item.status === 'active' && i === arr.findLastIndex(a => a.type === 'thinking' && a.status === 'active')
                  ? { ...item, status: 'done', duration_ms: event.data.duration_ms || null }
                  : item
              );
              updatedParts[toolIndex] = { ...prev, streaming_activity: activity };
              return updatedParts;
            });
          }
          break;

        case 'sub_agent_end':
          // Sub-agent finished — clean up streaming state and add tool_result
          // with the full trace of the sub-agent's work.
          if (currentPart) finalizeCurrentPart();
          inSubAgent = false;
          updateMessage((messageParts) => {
            // Slice back to where the sub-agent started, removing any
            // intermediate parts added during execution
            const kept = subAgentPartsStartIndex >= 0
              ? messageParts.slice(0, subAgentPartsStartIndex)
              : messageParts;

            // Clear streaming_content from the tool_use part now that we have the result
            const final = kept.map(p => {
              if (p.type === 'tool_use' && p.tool_use_id === event.data.tool_use_id) {
                // eslint-disable-next-line no-unused-vars
                const { streaming_content, ...rest } = p;
                return rest;
              }
              return p;
            });

            // Strip tool_result items from trace (contain massive raw provider data)
            // and keep only thinking, tool_use, and answer entries for rendering
            const rawTrace = event.data.trace || [];
            const cleanTrace = rawTrace
              .filter(t => t.type !== 'tool_result')
              .map(t => {
                if (t.type === 'tool_use' && t.parameters) {
                  // Truncate long parameter values to prevent storage bloat
                  const cleanParams = {};
                  for (const [k, v] of Object.entries(t.parameters)) {
                    cleanParams[k] = typeof v === 'string' && v.length > 200
                      ? v.slice(0, 200) + '…'
                      : v;
                  }
                  return { ...t, parameters: cleanParams };
                }
                return t;
              });

            return [...final, {
              type: 'tool_result',
              tool_use_id: event.data.tool_use_id,
              name: event.data.name,
              result: event.data.result,
              trace: cleanTrace,
              metadata: { leg_index: currentLegIndex },
            }];
          });
          subAgentPartsStartIndex = -1;
          break;

        case 'leg_start':
          currentLegIndex = event.data.leg_index || 1;
          break;

        case 'leg_end':
          // No action needed - leg_start of next leg will update the index
          break;

        case 'metadata':
          // Update usage_log in metadata
          if (event.data.type === 'message_end' && event.data.usage) {
            setMessages(prevMessages => {
              const msgIndex = prevMessages.findIndex(m => m.id === messageIdRef.current);
              if (msgIndex === -1) return prevMessages;

              const newMessages = prevMessages.slice();
              const prevMsg = prevMessages[msgIndex];

              newMessages[msgIndex] = {
                ...prevMsg,
                metadata: {
                  ...(prevMsg.metadata || {}),
                  usage_log: event.data.usage
                }
              };

              return newMessages;
            });
          }
          break;

        case 'error':
          throw new LlmProcessingError(event.data.error);

        case 'done':
          // Finalize any remaining part
          finalizeCurrentPart();
          break;

        default:
          console.warn('Unknown event type:', event.type);
      }
    } catch (error) {
      if (error.name === 'LlmProcessingError') {
        throw error;
      }
      console.error({event, error});
      handleStreamError(error);
    }
  };

  // Return the actual parser function
  return async function parseSSEStream(reader, decoder, restoreInput) {
    let buffer = ''; // Holds chunks of decoded text, potentially incomplete lines/events
    let currentEventType = null; // Stores the 'event:' type of the message being processed
    let currentEventData = ''; // Accumulates 'data:' lines for the current event

    /**
     * Helper function to parse, structure, and dispatch a completed SSE event.
     * Handles JSON parsing for specific event types.
     */
    const processAndDispatchEvent = (eventType, eventDataString) => {
      // Trim trailing newline potentially added during multi-line data accumulation
      const data = eventDataString.trimEnd();

      try {
        // Use the typed error
        const payload = parseSSEEventData(eventType, data);
        // Call the main event processor function with the structured event
        processSSEEvent({ type: eventType, data: payload });

        if (eventType === 'done') {
          return 'done';
        }

        return null;
      } catch (error) {
        // Let LlmProcessingError and AbortError propagate as-is
        // so callers can handle them properly (e.g. show renderMsg in toast)
        if (error.name === 'LlmProcessingError' || error.name === 'AbortError') {
          throw error;
        }
        console.error('Failed to parse event type JSON:', error);
        throw new Error(`Failed to parse event type JSON: ${error.message}`);
      }
    };

    // --- Main Stream Reading Loop ---
    try {
      while (true) {
        // Read the next chunk from the stream
        const { value, done } = await reader.read();

        if (done) {
          // If there's a partial event buffered, try to process it
          if (currentEventType && currentEventData) {
            console.warn("Stream ended with buffered event data, attempting to process.");
            processAndDispatchEvent(currentEventType, currentEventData);
          }

          // If stream ends with incomplete part, treat as error
          if (currentPart) {
            throw new Error('Stream ended with incomplete message part');
          }

          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let lineEndIndex;
        while ((lineEndIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.substring(0, lineEndIndex).trim();
          buffer = buffer.substring(lineEndIndex + 1);

          if (line === '') {
            // Empty line marks end of event block
            if (currentEventType && currentEventData) {
              const status = processAndDispatchEvent(currentEventType, currentEventData);
              if (status === 'done') {
                return;
              }
            }
            currentEventType = null;
            currentEventData = '';
          } else if (line.startsWith('event:')) {
            currentEventType = line.substring(6).trim();
            currentEventData = '';
          } else if (line.startsWith('data:')) {
            const dataLine = line.substring(5);
            if (currentEventData) {
              currentEventData += '\n' + dataLine;
            } else {
              currentEventData = dataLine;
            }
          }
        }
      }
    } catch (error) {
      handleStreamError(error, restoreInput);
    }
  };
}